import { DeviceManifest } from '@rapoo/device-sdk'
import {
  gcomeApKeyboardConfig,
  itineraryGcomeAp,
  keyboardMacro,
  keyboardPerfGcomeAp,
} from './generate/capabilityPresets'
import {
  gcomeApAudioRhythmLightingEffects,
  gcomeApKeyboardLightingEffects,
  gcomeApSideLightingEffects,
} from './generate/lightingPresets'

/**
 * Ace 68 Turbo 设备清单
 */
const rapooAce68Turbo: DeviceManifest = {
  channel: ['global'],
  model: 'Ace 68 Turbo',
  mainControl: 'Ace 68 Turbo',
  type: 'keyboard',
  version: '1.0.0',
  hardware: {
    vendorId: 0x3837,
    productId: [0x3026],
    currentPid: 0x3026,
    transport: 'HID',
    filters: [
      {
        usagePage: 1,
        usage: 0,
        description: 'Configuration Interface (Secondary)',
        type: 'default',
      },
      {
        // 与 VT68 同芯片 Bootloader（官方 EXE BusHound：0x19F5:0xAF7A / FF00:1 / 512B）
        // vendorId: 0x19f5,
        // productId: 0xaf7a,
        usagePage: 0xff00,
        usage: 0x01,
        description: 'GCOME_AP Bootloader Firmware Update Interface',
        productName: 'Volt* Upgrader',
        type: 'update',
      },
    ],
  },
  driver: 'GCOME_AP',
  ui: {
    assetsDir: 'manifests/Ace 68 Turbo',
    colors: {
      series: [
        {
          name: 'Default',
          value: '#1f2937',
          key: '',
        },
      ],
      current: 'Default',
    },
    icon: 'Hardware-Wireframe-Keyboard',
    overview: false,
  },
  capabilities: [
    gcomeApKeyboardConfig,
    {
      type: 'lighting',
      config: {
        enable: true,
        canAdd: false,
        effects: [
          ...gcomeApKeyboardLightingEffects,
          ...gcomeApSideLightingEffects,
          ...gcomeApAudioRhythmLightingEffects,
        ],
      },
    },
    itineraryGcomeAp,
    {
      type: 'keyFeatures',
      config: {
        advanced: true,
      },
    },
    keyboardPerfGcomeAp,
    keyboardMacro,
  ],
  layout: {
    image: 'frame',
    top: 18,
    bottom: 16,
    left: 16,
    right: 16,
    unit: 40,
    /**
     * key 值为固件 LED 定义区按键索引（0-127），
     * 由 CMD 0x07 GetDefaultKeyMatrix 读取的真实按键矩阵得到，
     * 注意：固件索引按物理矩阵列扫描排列，与视觉行列顺序不同！
     *
     * 修饰键按 HID 标准修饰位图（Code0）映射：
     *   Ctrl_L=8(0x01)  Shift_L=6(0x02)  Alt_L=28(0x04)  Win_L=18(0x08)
     *   Ctrl_R=68(0x10) Shift_R=47(0x20) Alt_R=48(0x40)  Fn=58(0xf0)
     * ⚠️ 0x08=左Win，0x20=右Shift，切勿混淆。
     */
    keys: [
      [
        {
          key: 0,
          keywords: 'Esc',
        },
        {
          key: 10,
          keywords: '!_1',
        },
        {
          key: 20,
          keywords: '@_2',
        },
        {
          key: 30,
          keywords: '#_3',
        },
        {
          key: 40,
          keywords: '$_4',
        },
        {
          key: 50,
          keywords: '%_5',
        },
        {
          key: 60,
          keywords: '^_6',
        },
        {
          key: 70,
          keywords: '&_7',
        },
        {
          key: 1,
          keywords: '*_8',
        },
        {
          key: 11,
          keywords: '(_9',
        },
        {
          key: 21,
          keywords: ')_0',
        },
        {
          key: 31,
          keywords: '__-',
        },
        {
          key: 41,
          keywords: '+_=',
        },
        {
          key: 51,
          keywords: 'BackSpace',
          w: 2,
        },
        {
          key: 71,
          keywords: 'Insert',
        },
      ],
      [
        {
          key: 2,
          keywords: 'Tab',
          y: 0.1,
          w: 1.5,
        },
        {
          key: 12,
          keywords: 'Q',
          y: 0.1,
        },
        {
          key: 22,
          keywords: 'W',
          y: 0.1,
        },
        {
          key: 32,
          keywords: 'E',
          y: 0.1,
        },
        {
          key: 42,
          keywords: 'R',
          y: 0.1,
        },
        {
          key: 52,
          keywords: 'T',
          y: 0.1,
        },
        {
          key: 62,
          keywords: 'Y',
          y: 0.1,
        },
        {
          key: 72,
          keywords: 'U',
          y: 0.1,
        },
        {
          key: 3,
          keywords: 'I',
          y: 0.1,
        },
        {
          key: 13,
          keywords: 'O',
          y: 0.1,
        },
        {
          key: 23,
          keywords: 'P',
          y: 0.1,
        },
        {
          key: 33,
          keywords: '{_[',
          y: 0.1,
        },
        {
          key: 43,
          keywords: '}_]',
          y: 0.1,
        },
        {
          key: 53,
          keywords: '|_\\',
          y: 0.1,
          w: 1.5,
        },
        {
          key: 63,
          keywords: 'Delete',
          y: 0.1,
        },
      ],
      [
        {
          key: 4,
          keywords: 'CapsLock',
          y: 0.1,
          w: 1.75,
        },
        {
          key: 14,
          keywords: 'A',
          y: 0.1,
        },
        {
          key: 24,
          keywords: 'S',
          y: 0.1,
        },
        {
          key: 34,
          keywords: 'D',
          y: 0.1,
        },
        {
          key: 44,
          keywords: 'F',
          y: 0.1,
        },
        {
          key: 54,
          keywords: 'G',
          y: 0.1,
        },
        {
          key: 64,
          keywords: 'H',
          y: 0.1,
        },
        {
          key: 74,
          keywords: 'J',
          y: 0.1,
        },
        {
          key: 5,
          keywords: 'K',
          y: 0.1,
        },
        {
          key: 15,
          keywords: 'L',
          y: 0.1,
        },
        {
          key: 25,
          keywords: ':_;',
          y: 0.1,
        },
        {
          key: 35,
          keywords: '"_\'',
          y: 0.1,
        },
        {
          key: 45,
          keywords: 'Enter',
          y: 0.1,
          w: 2.25,
        },
        {
          key: 55,
          keywords: 'PgUp',
          y: 0.1,
        },
      ],
      [
        {
          key: 6,
          keywords: 'Shift_L',
          y: 0.1,
          w: 2.25,
        },
        {
          key: 26,
          keywords: 'Z',
          y: 0.1,
        },
        {
          key: 36,
          keywords: 'X',
          y: 0.1,
        },
        {
          key: 46,
          keywords: 'C',
          y: 0.1,
        },
        {
          key: 56,
          keywords: 'V',
          y: 0.1,
        },
        {
          key: 66,
          keywords: 'B',
          y: 0.1,
        },
        {
          key: 76,
          keywords: 'N',
          y: 0.1,
        },
        {
          key: 7,
          keywords: 'M',
          y: 0.1,
        },
        {
          key: 17,
          keywords: '<_,',
          y: 0.1,
        },
        {
          key: 27,
          keywords: '>_.',
          y: 0.1,
        },
        {
          key: 37,
          keywords: '?_/',
          y: 0.1,
        },
        {
          key: 47,
          keywords: 'Shift_R',
          y: 0.1,
          w: 1.75,
        },
        {
          key: 57,
          keywords: '↑',
          y: 0.1,
        },
        {
          key: 65,
          keywords: 'PgDn',
          y: 0.1,
        },
      ],
      [
        {
          key: 8,
          keywords: 'Ctrl_L',
          y: 0.1,
          w: 1.25,
        },
        {
          key: 18,
          keywords: 'Win_L',
          y: 0.1,
          w: 1.25,
        },
        {
          key: 28,
          keywords: 'Alt_L',
          y: 0.1,
          w: 1.25,
        },
        {
          key: 38,
          keywords: 'Space_L',
          y: 0.1,
          w: 6.25,
          /** 空格长键额外 4 个 LED 灯珠（扫描确认）：主灯 38 + 82/83/84/85 共 5 灯 */
          ledIndexes: [82, 83, 84, 85],
        },
        {
          key: 48,
          keywords: 'Alt_R',
          y: 0.1,
          w: 1,
        },
        {
          key: 58,
          keywords: 'Fn',
          y: 0.1,
          w: 1,
        },
        {
          key: 68,
          keywords: 'Ctrl_R',
          y: 0.1,
          w: 1,
        },
        {
          key: 78,
          keywords: '←',
          y: 0.1,
        },
        {
          key: 67,
          keywords: '↓',
          y: 0.1,
        },
        {
          key: 77,
          keywords: '→',
          y: 0.1,
        },
      ],
    ],
  },
}

export default rapooAce68Turbo
